import { Pagination } from '@admitkard/ccl/Pagination';
import React from 'react';
import UniversitySearch from './UniversitySearch';
import { UniversityData } from './UniversityTypes';
import { UniversityCard } from '../UniversityCard';
import { Skeleton } from '@admitkard/ccl/Skeleton';
import { UniversityListingCss } from './UniversityListingContainer.css';
import { Text } from '@admitkard/ccl/Text';
import { Link } from '@admitkard/ccl/Link';
import { Select } from '@admitkard/ccl/Select';
import { universitySortByRank, universitySortByName } from './UniversityListingUtils';
import { UniversityCountryDropdown } from './UniversityCountryDropdown';
import { IconName } from '@admitkard/ccl/Icon';

const CARDS_IN_ONE_PAGE = 25;

interface UniversityListingContainerProps {
  universityList: UniversityData[];
  countryToSlugMapping: Record<string, IconName>;
}

class UniversityListingContainer extends React.Component<UniversityListingContainerProps> {
  state = {
    currentListings: this.props.universityList.slice(0, 25),
    currentPage: 1,
    totalPages: Math.ceil(this.props.universityList.length / CARDS_IN_ONE_PAGE),
    isLoading: false,
    isSearching: false,
    searchKeyword: '',
    sortValue: 0,
    dropdownCountryValue: 'flag',
  };

  allUniversityListing = [...this.props.universityList];

  showLoader = () => {
    this.setState({ isLoading: true }, () => {
      setTimeout(() => {
        this.setState({ isLoading: false });
      }, 500);
    });
  };

  handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    this.showLoader();
    window.scrollTo(0, 0);
    const startIndex = CARDS_IN_ONE_PAGE * (value - 1);
    const newCurrentListings = this.allUniversityListing.slice(startIndex, startIndex + CARDS_IN_ONE_PAGE);
    this.setState({ currentPage: value, currentListings: newCurrentListings });
  };

  handleSearch = (searchString: string) => {
    this.showLoader();
    searchString = searchString.toLowerCase();
    this.allUniversityListing = this.getFilteredUniversities(
      this.props.universityList,
      this.state.sortValue,
      searchString,
      this.state.dropdownCountryValue
    );
    const newCurrentListings = this.allUniversityListing.slice(0, CARDS_IN_ONE_PAGE);
    const newTotalPages = Math.ceil(this.allUniversityListing.length / CARDS_IN_ONE_PAGE);
    this.setState({
      currentPage: 1,
      currentListings: newCurrentListings,
      totalPages: newTotalPages,
      searchKeyword: searchString,
      isSearching: true,
    });
  };

  handleCountryFilter = (event: React.ChangeEvent<{ value: unknown }>) => {
    this.showLoader();
    const changedCountryValue = event.target.value;
    this.allUniversityListing = this.getFilteredUniversities(
      this.props.universityList,
      this.state.sortValue,
      this.state.searchKeyword,
      changedCountryValue as string
    );
    const newCurrentListings = this.allUniversityListing.slice(0, 25);
    const newTotalPages = Math.ceil(this.allUniversityListing.length / CARDS_IN_ONE_PAGE);
    this.setState({
      dropdownCountryValue: event.target.value,
      currentListings: newCurrentListings,
      currentPage: 1,
      totalPages: newTotalPages,
    });
  };

  handleClearAll = () => {
    this.allUniversityListing = [...this.props.universityList];
    this.setState({
      isSearching: false,
      searchKeyword: '',
      currentListings: this.props.universityList.slice(0, 25),
      currentPage: 1,
      totalPages: Math.ceil(this.props.universityList.length / CARDS_IN_ONE_PAGE),
      sortValue: 0,
      dropdownCountryValue: 'flag',
    });
    this.showLoader();
  };

  getFilteredUniversities = (
    universityList: UniversityData[],
    sortOnBasisValue: number,
    searchString = '',
    changedCountryValue = 'flag'
  ) => {
    if (!(changedCountryValue === 'flag')) {
      universityList = universityList.filter((university) => university.country && university.country.slug === changedCountryValue);
    }
    universityList = universityList.filter((university) => university.name && university.name.toLowerCase().includes(searchString));
    switch (sortOnBasisValue) {
      case 0:
        universityList.sort(universitySortByRank);
        break;
      case 1:
        universityList.sort(universitySortByName);
        break;
      default:
        break;
    }
    return universityList;
  };

  handleSortFilter = (event: React.ChangeEvent<{ value: unknown }>) => {
    this.showLoader();
    const sortOnBasis = event.target.value as number;
    this.allUniversityListing = this.getFilteredUniversities(
      this.props.universityList,
      sortOnBasis,
      this.state.searchKeyword,
      this.state.dropdownCountryValue
    );
    const newCurrentListings = this.allUniversityListing.slice(0, 25);
    const newTotalPages = Math.ceil(this.allUniversityListing.length / CARDS_IN_ONE_PAGE);
    this.setState({
      currentPage: 1,
      currentListings: newCurrentListings,
      totalPages: newTotalPages,
      sortValue: sortOnBasis,
    });
  };

  render() {
    const { isLoading, searchKeyword, isSearching, sortValue } = this.state;
    const totalUniversitiesFoundCount = this.allUniversityListing.length;
    return (
      <>
        <div className="university-listing-container">
          <UniversitySearch handleSearch={this.handleSearch} />
          <div className="university-filter-container">
            <UniversityCountryDropdown
              countryToSlugMapping={this.props.countryToSlugMapping}
              handleCountryChange={this.handleCountryFilter}
              dropdownCountryValue={this.state.dropdownCountryValue}
            />
            <div className="university-sort-select">
              <Select
                size="small"
                value={sortValue.toString()}
                onChange={this.handleSortFilter}
                label="Sort By"
                items={['Rank', 'A - Z']}
              ></Select>
            </div>
          </div>
          <div className="university-listings">
            {isSearching && !isLoading && (
              <div className="search-details">
                <div className="clear-all-link">
                  <Link onClick={this.handleClearAll} color="primary">
                    Clear all
                  </Link>
                </div>
                {totalUniversitiesFoundCount ? (
                  <Text lightness={7} align="left">
                    {totalUniversitiesFoundCount} results found {searchKeyword ? ` for "${searchKeyword}"` : ''}
                  </Text>
                ) : (
                  <Text lightness={7} align="left">
                    No results found {searchKeyword ? ` for "${searchKeyword}"` : ''}
                  </Text>
                )}
              </div>
            )}
            {this.state.currentListings.map((university, index) => {
              if (!university.name) {
                return;
              }
              if (isLoading === false) {
                return (
                  <UniversityCard
                    key={university._id}
                    name={university.name || ''}
                    rank={university.universityCountryRank}
                    countryName={university.country ? university.country.name : ''}
                    logoUrl={university.logoUrl || ''}
                    city={university.city ? university.city.name || '' : ''}
                    universitySlug={university.slug || ''}
                    countrySlug={university.country ? university.country.slug : ''}
                    allCampus={university.allCampusList || []}
                  />
                );
              } else {
                return (
                  <div key={index} className="custom-skeleton">
                    <Skeleton animation="wave" variant="rect" height={90} />
                  </div>
                );
              }
            })}
          </div>
          <div className="pagination-container">
            <Pagination
              onChange={this.handlePageChange}
              size="small"
              page={this.state.currentPage}
              siblingCount={2}
              boundaryCount={1}
              count={this.state.totalPages}
              color="primary"
            />
          </div>
        </div>
        <style jsx>{UniversityListingCss}</style>
      </>
    );
  }
}

export default UniversityListingContainer;
